"use client";

import { Shield, TrendingUp, Users, Zap, Lock, HelpCircle } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "next-intl";
import type { ServiceGuidance } from "@/lib/service-guidance";

type Props = {
    locale: string;
    guidance?: ServiceGuidance;
    guidanceKey?: string;
};

/**
 * Explanatory copy comes from the homepage `seo` namespace or, on commerce pages,
 * from server-provided `guidance`; `serviceSeoContent` is dead data.
 *
 * This used to call `getSpunSeoContent`, which built the text by picking sentence
 * variants out of per-locale banks with a seeded PRNG, so that every
 * platform × service × sub-category URL got its own reshuffled prose. Google's spam
 * policy names that pattern directly under scaled content abuse — "automated
 * transformations like synonymizing … where little value is provided to users" — and
 * the Portuguese bank had degraded into text that does not parse as language
 * ("Leia Um Ressecamento Lógico Para…", "Operações Desencolvidas Totalmente Em
 * Privado").
 *
 * These pages do not need invented uniqueness: they are already differentiated by
 * real catalog data — actual packages, real prices in the visitor's currency, tier
 * labels and per-service FAQs. What the prose has to do is explain the service
 * honestly, once, in the reader's language.
 *
 * WHAT THIS COMPONENT ACTUALLY READS (an earlier version of this comment claimed
 * `serviceSeoContent`; it does not, and that sentence sent three separate audits to
 * the wrong file):
 *   - commerce pages pass `guidance` (lib/service-guidance.ts) and it always wins,
 *     because getServiceGuidance() never returns undefined — the `?? tGlobal(...)`
 *     fallback below is unreachable on those 96 routes;
 *   - the homepage passes no guidance, so it renders the `seo` namespace.
 * `messages/*.json → serviceSeoContent` is therefore DEAD DATA: nothing imports it.
 * Do not "fix" copy there expecting it to ship, and do not wire it up without first
 * auditing its claims — it still carries the absolute promises that were removed
 * from `seo` in v71.
 *
 * Verifying whether a string is live: fetch the page and compare its index against
 * `html.indexOf("self.__next_f.push")`. A match AFTER that offset is only the RSC
 * payload and is not visible to a reader or rendered for a crawler.
 */
export default function SEOContent({ locale: _locale, guidance, guidanceKey }: Props) {
    const tGlobal = useTranslations("seo");
    const [isExpanded, setIsExpanded] = useState(false);

    // Service pages receive server-selected, metric-specific copy. Keeping the
    // locale matrix on the server avoids shipping every language to the browser.
    const isDynamic = !!guidance;
    const title = guidance?.title ?? tGlobal("title");
    const intro = guidance?.intro ?? tGlobal("intro");
    const article1 = guidance?.main.article1 ?? {
        title: tGlobal("main.article1.title"),
        content: tGlobal("main.article1.content"),
        content2: tGlobal("main.article1.content2"),
    };
    const article2 = guidance?.main.article2 ?? {
        title: tGlobal("main.article2.title"),
        content: tGlobal("main.article2.content"),
        list: tGlobal.raw("main.article2.list") as string[],
    };
    const hidden1 = guidance?.hidden.article1 ?? {
        title: tGlobal("hidden.article1.title"),
        content: tGlobal("hidden.article1.content"),
    };
    const hidden2 = guidance?.hidden.article2 ?? {
        title: tGlobal("hidden.article2.title"),
        content: tGlobal("hidden.article2.content"),
    };

    // The visible keyword cloud that used to be built here has been removed — see the
    // note further down where it was rendered. R1 (A-08) also removed <meta name="keywords">
    // from every page; no keyword list reaches the page in any form.

    return (
        <section
            className="py-16 bg-white border-t border-slate-100 defer-render"
            style={{ containIntrinsicSize: "auto 1620px" }}
            data-service-guidance={isDynamic ? "true" : undefined}
            data-service-key={isDynamic ? guidanceKey : undefined}
        >
            <div className="container-custom max-w-5xl">
                <div className="prose prose-slate prose-lg max-w-none">
                    {/* H2 for Primary Keyword Targeting */}
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
                        {title}
                    </h2>

                    {/* Intro Text */}
                    <div className="text-slate-600 leading-relaxed mb-10 text-center max-w-3xl mx-auto">
                        <p>
                            {intro}
                        </p>
                    </div>

                    {/* SEO Grid Content */}
                    <div className="grid md:grid-cols-2 gap-12 mt-12">
                        {/* Section 1 */}
                        <article>
                            <h3 className="flex items-center gap-3 text-2xl font-bold text-slate-800 mb-4">
                                <TrendingUp className="w-6 h-6 text-emerald-500" />
                                {article1.title}
                            </h3>
                            <div className="text-slate-600 space-y-4">
                                <p>
                                    {article1.content}
                                </p>
                                <p>
                                    {article1.content2}
                                </p>
                            </div>
                        </article>

                        {/* Section 2 */}
                        <article>
                            <h3 className="flex items-center gap-3 text-2xl font-bold text-slate-800 mb-4">
                                <Shield className="w-6 h-6 text-emerald-500" />
                                {article2.title}
                            </h3>
                            <div className="text-slate-600 space-y-4">
                                <p>
                                    {article2.content}
                                </p>
                                <ul className="space-y-2 list-none pl-0">
                                    <li className="flex items-center gap-2">
                                        <Lock className="w-4 h-4 shrink-0 text-emerald-500" />
                                        {article2.list[0]}
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Users className="w-4 h-4 shrink-0 text-emerald-500" />
                                        {article2.list[1]}
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Zap className="w-4 h-4 shrink-0 text-emerald-500" />
                                        {article2.list[2]}
                                    </li>
                                </ul>
                            </div>
                        </article>
                    </div>

                    {/* Collapsible Deep Content for Hard SEO */}
                    <div className="mt-12 border-t border-slate-100 pt-8">
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                        aria-expanded={isExpanded} aria-controls="scope-details"
                            className="flex items-center gap-2 py-2 text-emerald-800 font-bold hover:text-emerald-700 transition-colors mx-auto"
                        >
                            {isExpanded
                                ? (guidance?.buttonShowLess ?? tGlobal("buttonShowLess"))
                                : (guidance?.buttonShowMore ?? tGlobal("buttonShowMore"))}
                            <HelpCircle className="w-4 h-4" />
                        </button>

                        {/* Always in the DOM (hidden, not unmounted): crawlers do not click
                            the toggle, so conditional mounting kept this six-locale prose out
                            of the indexable HTML entirely. */}
                        <div id="scope-details" hidden={!isExpanded}
                            className="mt-8 grid md:grid-cols-2 gap-12 animate-in fade-in slide-in-from-top-4 duration-500">
                                <article>
                                    <h3 className="text-xl font-bold text-slate-800 mb-3">
                                        {hidden1.title}
                                    </h3>
                                    <p className="text-slate-600 mb-4">
                                        {hidden1.content}
                                    </p>
                                </article>

                                <article>
                                    <h3 className="text-xl font-bold text-slate-800 mb-3">
                                        {hidden2.title}
                                    </h3>
                                    <p className="text-slate-600 mb-4">
                                        {hidden2.content}
                                    </p>
                                </article>
                                {!isDynamic && (
                                    <>
                                        <article>
                                            <h3 className="text-xl font-bold text-slate-800 mb-3">
                                                {tGlobal("hidden.article3.title")}
                                            </h3>
                                            <p className="text-slate-600 mb-4">
                                                {tGlobal("hidden.article3.content")}
                                            </p>
                                        </article>

                                        <article>
                                            <h3 className="text-xl font-bold text-slate-800 mb-3">
                                                {tGlobal("hidden.article4.title")}
                                            </h3>
                                            <p className="text-slate-600 mb-4">
                                                {tGlobal("hidden.article4.content")}
                                            </p>
                                        </article>
                                    </>
                                )}
                            </div>
                    </div>

                    {/* REMOVED — visible keyword cloud.
                        This rendered a bare comma-separated list of 18+ keyword variants
                        ("X Follower kaufen, X Likes kaufen, X Views kaufen, …") styled at
                        text-[11px] in slate-300 on white: de-emphasised so readers would
                        not notice it, but present for crawlers.

                        That is two separate spam-policy problems at once — keyword
                        stuffing, and text hidden from users but served to search engines
                        — plus a contrast failure. Neither is worth the (nil) ranking
                        benefit: Google has ignored keyword lists as a ranking signal for
                        well over a decade.

                        R1 (A-08) removed <meta name="keywords"> as well (G-META-KW gate).
                        Do not reintroduce a visible list or a keywords meta. */}
                </div>
            </div>
        </section>
    );
}
